import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { db } from '../lib/firebase';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import {
  Truck, Phone, MapPin, Clock, Trash2, Pencil, Search, X, Loader2, Plus, UserCheck
} from 'lucide-react';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../components/ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const statusColors = {
  available: 'text-green-400 bg-green-400/10 border-green-400/20',
  on_job: 'text-blue-400 bg-blue-400/10 border-blue-400/20',
  off_duty: 'text-slate-400 bg-slate-400/10 border-slate-400/20',
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const AdminDriversTab = () => {
  const [drivers, setDrivers] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', status: 'available', location: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const q = query(collection(db, 'drivers'), orderBy('created_at', 'desc'));
    const unsub = onSnapshot(q, (snap) => {
      setDrivers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => console.error('Drivers listener error:', err));
    return unsub;
  }, []);

  const filtered = drivers.filter(d => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.name?.toLowerCase().includes(q) || d.phone?.includes(q);
  });

  const resetForm = () => { setForm({ name: '', phone: '', status: 'available', location: '' }); setEditId(null); setShowForm(false); };

  const handleEdit = (d) => {
    setForm({ name: d.name, phone: d.phone, status: d.status || 'available', location: d.location || '' });
    setEditId(d.id); setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editId) {
        await axios.put(`${API}/firestore/drivers/${editId}`, form, { withCredentials: true });
      } else {
        await axios.post(`${API}/firestore/drivers`, form, { withCredentials: true });
      }
      resetForm();
    } catch (err) {
      console.error('Save failed:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/firestore/drivers/${id}`, { withCredentials: true });
    } catch (err) {
      console.error('Delete failed:', err);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      await axios.put(`${API}/firestore/drivers/${id}`, { status: newStatus }, { withCredentials: true });
    } catch (err) {
      console.error('Status update failed:', err);
    }
  };

  return (
    <div>
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-brand-red/10 flex items-center justify-center">
            <Truck className="w-5 h-5 text-brand-red" />
          </div>
          <div>
            <h2 className="font-heading text-xl text-white">Drivers</h2>
            <p className="text-slate-500 text-xs">{drivers.length} total &middot; Firestore real-time</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..."
              className="pl-10 pr-4 py-2 bg-brand-surface border border-white/10 rounded-lg text-white placeholder-slate-600 text-sm focus:border-brand-red outline-none w-48"
              data-testid="driver-search" />
          </div>
          <button onClick={() => { resetForm(); setShowForm(true); }}
            className="flex items-center gap-2 px-4 py-2 bg-brand-red hover:bg-red-700 text-white rounded-lg text-sm font-medium transition-colors"
            data-testid="add-driver-btn">
            <Plus className="w-4 h-4" /> Add Driver
          </button>
        </div>
      </div>

      {showForm && (
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
          className="bg-brand-surface rounded-xl border border-white/10 p-6 mb-6" data-testid="driver-form">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-white">{editId ? 'Edit Driver' : 'New Driver'}</h3>
            <button onClick={resetForm} className="text-slate-500 hover:text-white"><X className="w-5 h-5" /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <input type="text" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Full name" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="driver-name-input" />
            <input type="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))} placeholder="Phone" required
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="driver-phone-input" />
            <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white focus:border-brand-red outline-none text-sm appearance-none" data-testid="driver-status-input">
              <option value="available" className="bg-brand-navy">Available</option>
              <option value="on_job" className="bg-brand-navy">On Job</option>
              <option value="off_duty" className="bg-brand-navy">Off Duty</option>
            </select>
            <input type="text" value={form.location} onChange={e => setForm(p => ({ ...p, location: e.target.value }))} placeholder="Location"
              className="px-4 py-2.5 bg-white/5 border border-white/10 rounded-lg text-white placeholder-slate-600 focus:border-brand-red outline-none text-sm" data-testid="driver-location-input" />
            <div className="sm:col-span-2 lg:col-span-4 flex justify-end">
              <button type="submit" disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-brand-red hover:bg-red-700 text-white rounded-lg text-sm font-medium disabled:opacity-50 transition-colors" data-testid="driver-save-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserCheck className="w-4 h-4" />}
                {editId ? 'Update' : 'Create'}
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {filtered.length === 0 ? (
        <div className="text-center py-20"><Truck className="w-12 h-12 text-slate-600 mx-auto mb-4" /><p className="text-slate-500">No drivers found</p></div>
      ) : (
        <div className="space-y-3">
          {filtered.map((d, i) => (
            <motion.div key={d.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.02 }}
              className="bg-brand-surface rounded-xl border border-white/10 p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`driver-row-${d.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-white font-medium">{d.name}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${statusColors[d.status] || statusColors.off_duty}`}>{d.status}</span>
                </div>
                <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm mt-1">
                  <span className="text-slate-400 flex items-center gap-1"><Phone className="w-3 h-3" />{d.phone}</span>
                  {d.location && <span className="text-slate-400 flex items-center gap-1"><MapPin className="w-3 h-3" />{d.location}</span>}
                  {d.current_job_id && <span className="text-blue-400 text-xs">Job: {d.current_job_id.slice(0,8)}...</span>}
                  <span className="text-slate-600 flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(d.created_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <Select value={d.status} onValueChange={(val) => handleStatusChange(d.id, val)}>
                  <SelectTrigger className="w-[120px] bg-white/5 border-white/10 text-white text-xs" data-testid={`driver-status-select-${d.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-brand-navy border-white/10">
                    <SelectItem value="available" className="text-white hover:bg-white/10">Available</SelectItem>
                    <SelectItem value="on_job" className="text-white hover:bg-white/10">On Job</SelectItem>
                    <SelectItem value="off_duty" className="text-white hover:bg-white/10">Off Duty</SelectItem>
                  </SelectContent>
                </Select>
                <button onClick={() => handleEdit(d)} className="p-2 rounded-lg bg-white/5 text-slate-400 hover:text-white hover:bg-white/10 transition-colors" data-testid={`edit-driver-${d.id}`}>
                  <Pencil className="w-4 h-4" />
                </button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <button className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors" data-testid={`delete-driver-${d.id}`}><Trash2 className="w-4 h-4" /></button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-brand-navy border-white/10">
                    <AlertDialogHeader><AlertDialogTitle className="text-white">Delete Driver</AlertDialogTitle>
                      <AlertDialogDescription className="text-slate-400">Permanently delete {d.name}?</AlertDialogDescription></AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleDelete(d.id)} className="bg-red-600 text-white hover:bg-red-700">Delete</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminDriversTab;
